import { Component } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { AgreementViewModel } from '@lib/core';
import { cinema1ValidationPattern } from 'libs/core/src/app.const';
import { CinemaDataProvider } from 'libs/core/src/lib/data-provider/cinema.data-provider';
import { GenreDataProvider } from 'libs/core/src/lib/data-provider/genre.data-provider';
import { UserDataProvider } from 'libs/core/src/lib/data-provider/user.data-provider';
import { LoaderEnum } from 'libs/core/src/lib/enum/loader.enum';
import { CheckListItemModel } from 'libs/core/src/lib/model/checklistitem.model';
import { GenderService } from 'libs/core/src/lib/service/gender.service';
import { LoaderService } from 'libs/core/src/lib/service/loading.service';
import { AuthStateService } from 'libs/core/src/lib/state/auth.state.service';
import { UserProfileComponent } from 'libs/shared/src/lib/component/user/user-profile/user-profile.component';
import { forkJoin, Observable } from 'rxjs';

@Component({ standalone: false, selector: 'app-user-profile', templateUrl: './user-profile.component.html', styleUrls: ['./user-profile.component.scss'] })
export class CustomUserProfileComponent extends UserProfileComponent {
  favouriteCinemas: CheckListItemModel[];
  favouriteGenres: CheckListItemModel[];
  agreements: AgreementViewModel[] = [];

  constructor(
    protected userDataProvider: UserDataProvider,
    protected loaderService: LoaderService,
    protected authStateService: AuthStateService,
    protected cinemaDataProvider: CinemaDataProvider,
    protected genreDataProvider: GenreDataProvider,
    protected genderService: GenderService
  ) {
    super(userDataProvider, loaderService, authStateService, cinemaDataProvider);

    this.createForm();

    this.user$.subscribe(() => {
      this.onAfterInit().subscribe((res) => {
        this.favouriteCinemas = this.generateChecklistItemList(res.cinemas, res.favouriteCinemas);
        this.favouriteGenres = this.generateChecklistItemList(res.genres, res.favouriteGenres);
        this.agreements = res.agreements.marketingAgreements ?? [];

        const agreementsArray = this.profileForm.get('agreements') as FormArray;
        agreementsArray.clear();
        this.agreements.forEach((agreement) => {
          const isChecked =
            this.user.agreements?.some(
              (id) => id.toLowerCase() === agreement.id.toLowerCase() // ← case-insensitive
            ) ?? false;
          const control = new FormControl(isChecked);
          if (agreement.required) {
            control.setValidators(Validators.requiredTrue);
          }
          agreementsArray.push(control);
        });

        this.profileForm.setValue({
          gender: this.user.gender,
          firstName: this.user.firstName,
          lastName: this.user.lastName,
          email: this.user.email,
          phone: this.user.phone,
          favouriteCinemas: this.favouriteCinemas,
          favouriteGenres: this.favouriteGenres,
          birthday: this.user.birthday,
          agreements: agreementsArray.value
        });

        if (this.user.birthday && this.user.birthday?.year !== 1900) {
          this.profileForm.controls['birthday'].disable();
        }

        this.loaderService.hideLoader(LoaderEnum.MAIN);
      });
    });
  }

  onAfterInit(): Observable<any> {
    return forkJoin({
      cinemas: this.cinemaDataProvider.getCinemas(),
      genres: this.genreDataProvider.listViaApiModel(),
      favouriteCinemas: this.userDataProvider.getFavouriteCinemaList(),
      favouriteGenres: this.userDataProvider.getFavouriteGenreList(),
      agreements: this.userDataProvider.getAgreements()
    });
  }

  generateChecklistItemList(all: any[], favourites: any[]): any {
    const checkList = all.map((a) => new CheckListItemModel(a.id, a.name));
    checkList.forEach((c) => (c.checked = favourites?.some((f) => f.id === c.id)));
    return checkList;
  }

  createForm() {
    this.profileForm = new FormGroup({
      gender: new FormControl(0),
      firstName: new FormControl('', [Validators.required]),
      lastName: new FormControl('', [Validators.required]),
      email: new FormControl('', [Validators.required, Validators.pattern(cinema1ValidationPattern.email)]),
      phone: new FormControl('', [Validators.pattern(cinema1ValidationPattern.phone)]),
      favouriteCinemas: new FormControl(''),
      favouriteGenres: new FormControl(''),
      birthday: new FormControl(''),
      agreements: new FormArray([])
    });
  }

  get agreementsFormArray(): FormArray {
    return (this.profileForm?.get('agreements') as FormArray) ?? new FormArray([]);
  }

  public onSelect(event: any, prefName: string) {
    this.profileForm.controls['gender'].setValue(event.id);
  }
}
